import "./HelpCenterNavItem.scss"

import { useState } from "react"
import { Link } from "react-router-dom"

import Expander from "@/shared/components/extrinsic/Expander/Expander"
import Icon from "@/shared/components/intrinsic/Icon/Icon"
import { classWithModifiers } from "@/utils/common"

import { HelpCenterNavItemProps } from "../../types"

function HelpCenterNavItem(props: HelpCenterNavItemProps) {
  const { item, activeSectionId, onNavigate, currentPath, level, isFiltered } = props

  const hasChildren = item.children && item.children.length > 0
  const isCurrentPath = item.path === currentPath
  const isActive = item.anchorId ? activeSectionId === item.anchorId : isCurrentPath
  const isParentOfCurrentPath = item.path && currentPath.startsWith(item.path) && item.path !== currentPath

  // Auto-expand if: filtered, parent of current path, or current path (for items with both path and children)
  const [expanded, setExpanded] = useState(
    isFiltered || isParentOfCurrentPath || (isCurrentPath && hasChildren)
  )

  function handleClick() {
    // Navigate to path or anchor
    if (item.path) {
      onNavigate(item.anchorId || "", item.path)
    } else if (item.anchorId) {
      onNavigate(item.anchorId)
    } else if (hasChildren && itemType !== "subsection") {
      // For sections without paths (e.g., "POS Integrations"), toggle expansion
      setExpanded(!expanded)
    }
  }

  function toggleExpand(e: React.MouseEvent) {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  const itemType = item.type || "step-list"
  const isClickable = item.path || item.anchorId

  // Check if children are step-list items (for rendering circles/dots)
  const hasStepListChildren = hasChildren && item.children?.some(child => child.type === "step-list")

  const content = (
    <div
      className={classWithModifiers(
        "help-center-nav-item__content",
        isActive && "active",
        isParentOfCurrentPath && "parent-active",
        itemType
      )}
      data-level={level}
      data-has-children={hasChildren}
      data-type={itemType}
      onClick={isClickable ? handleClick : undefined}
    >
      {hasChildren && itemType === "subsection" && (
        <button
          className={classWithModifiers("help-center-nav-item__toggle", expanded && "expanded")}
          onClick={toggleExpand}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <Icon name="chevron-right" />
        </button>
      )}
      {itemType === "sub-item" && (
        <Icon name="arrow-left" className="help-center-nav-item__sub-arrow" />
      )}
      {itemType === "special" && item.icon && (
        <Icon name={item.icon} className="help-center-nav-item__special-icon" />
      )}
      <span className="help-center-nav-item__label">{item.label}</span>
    </div>
  )

  return (
    <li className={classWithModifiers("help-center-nav-item", itemType)}>
      {item.path && !item.anchorId ? (
        <Link to={item.path} className="help-center-nav-item__link">
          {content}
        </Link>
      ) : (
        content
      )}
      {hasChildren && (
        <Expander expanded={expanded || isFiltered}>
          <ul
            className={classWithModifiers(
              "help-center-nav-item__children",
              hasStepListChildren && "step-list"
            )}
          >
            {item.children!.map((child, index) => (
              <HelpCenterNavItem
                key={child.id}
                item={child}
                activeSectionId={activeSectionId}
                onNavigate={onNavigate}
                currentPath={currentPath}
                level={level + 1}
                isFiltered={isFiltered}
              />
            ))}
          </ul>
        </Expander>
      )}
    </li>
  )
}

export default HelpCenterNavItem
